import React, { useId } from 'react';

interface CanvasGridProps {
  panX: number;
  panY: number;
  zoom: number;
}

// Grid constants
const BASE_GRID_SIZE = 20; // 20px at 100% zoom
const MIN_GRID_SIZE = 8; // Minimum pixel size before grid becomes too dense
const MAX_GRID_SIZE = 100; // Maximum pixel size before grid becomes too sparse

export function CanvasGrid({ panX, panY, zoom }: CanvasGridProps) {
  // Use unique ID to avoid pattern conflicts across instances
  const patternId = useId();
  
  // Calculate grid size based on zoom with clamping
  const rawGridSize = BASE_GRID_SIZE * zoom;
  const gridSize = Math.max(MIN_GRID_SIZE, Math.min(MAX_GRID_SIZE, rawGridSize));
  
  // Calculate offset for seamless scrolling
  const offsetX = (panX * zoom) % gridSize;
  const offsetY = (panY * zoom) % gridSize;
  
  // Scale dot radius based on zoom for better visibility
  // Larger dots at lower zoom, smaller at higher zoom
  const dotRadius = Math.max(0.8, Math.min(1.5, 1 / Math.sqrt(zoom)));
  
  // Adjust opacity based on zoom level for consistent visibility
  const opacity = zoom < 0.5 ? 0.7 : zoom > 1.5 ? 0.4 : 0.5;

  // Single grid pattern - dots only
  return (
    <div 
      className="absolute inset-0 pointer-events-none overflow-hidden"
      style={{ zIndex: 0 }}
    >
      <svg 
        className="absolute inset-0" 
        width="100%" 
        height="100%"
        style={{ 
          display: 'block',
          isolation: 'isolate' // Prevents blending issues
        }}
      >
        <defs>
          <pattern
            id={patternId}
            width={gridSize}
            height={gridSize}
            patternUnits="userSpaceOnUse"
            x={offsetX}
            y={offsetY}
          >
            <circle
              cx={gridSize / 2}
              cy={gridSize / 2}
              r={dotRadius}
              fill="hsl(var(--canvas-grid))"
              fillOpacity={opacity}
            />
          </pattern>
        </defs>
        <rect 
          width="100%" 
          height="100%" 
          fill={`url(#${patternId})`}
        />
      </svg>
    </div>
  );
}
